import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Checkbox } from 'src/design/atoms/Checkbox';
import { TextInput } from 'src/design/atoms/TextInput';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons-react';
import type { MRT_ColumnDef, MRT_RowSelectionState } from 'mantine-react-table';
import React from 'react';
import { JobOptionDto } from 'src/api/FiretrolApiClient';
import { useJobSearch } from 'src/api/queries/jobs/useJobSearch';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';

const SOURCE_CTC = 'Ctc';

interface JobGrantSelectorProps {
  initialJobIds: number[];
  initialCtcJobNumbers: string[];
  onChange: (jobIds: number[], ctcJobNumbers: string[]) => void;
}

/** Stable composite row key that distinguishes CTC jobs (id=0) from Firetrol jobs. */
function rowKey(job: JobOptionDto): string {
  return job.source === SOURCE_CTC ? `ctc:${job.wsJobNumber}` : `job:${job.id}`;
}

/** Split composite keys back into typed arrays for the form. */
function splitKeys(keys: string[]): {
  jobIds: number[];
  ctcJobNumbers: string[];
} {
  const jobIds: number[] = [];
  const ctcJobNumbers: string[] = [];
  for (const key of keys) {
    if (key.startsWith('ctc:')) {
      ctcJobNumbers.push(key.slice(4));
    } else if (key.startsWith('job:')) {
      jobIds.push(Number(key.slice(4)));
    }
  }
  return { jobIds, ctcJobNumbers };
}

export const JobGrantSelector: React.FC<JobGrantSelectorProps> = ({
  initialJobIds,
  initialCtcJobNumbers,
  onChange,
}) => {
  const [search, setSearch] = React.useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [showOnlySelected, setShowOnlySelected] = React.useState(false);
  const isFirstRender = React.useRef(true);

  // DTOs for hydrated/checked rows — kept so selections stay visible across searches.
  const [selectedOptions, setSelectedOptions] = React.useState<JobOptionDto[]>(
    [],
  );

  // The initially-granted ids are captured once so the hydration query key
  // does not change as the user edits the selection.
  const hydrationArgs = React.useRef({
    ids: initialJobIds.length > 0 ? initialJobIds : null,
    ctcJobNumbers:
      initialCtcJobNumbers.length > 0 ? initialCtcJobNumbers : null,
  });

  const { data: hydrated } = useJobSearch({
    ids: hydrationArgs.current.ids,
    ctcJobNumbers: hydrationArgs.current.ctcJobNumbers,
    enabled:
      hydrationArgs.current.ids !== null ||
      hydrationArgs.current.ctcJobNumbers !== null,
  });

  const { data: searchData, isFetching: loading } = useJobSearch({
    query: debouncedSearch.trim() || null,
  });
  const searchResults = React.useMemo(() => searchData ?? [], [searchData]);

  const [rowSelection, setRowSelection] = React.useState<MRT_RowSelectionState>(
    () => {
      const initial: MRT_RowSelectionState = {};
      initialJobIds.forEach((id) => (initial[`job:${id}`] = true));
      initialCtcJobNumbers.forEach((n) => (initial[`ctc:${n}`] = true));
      return initial;
    },
  );

  React.useEffect(() => {
    if (!hydrated) return;
    setSelectedOptions((prev) => mergeByKey(prev, hydrated));
  }, [hydrated]);

  // Emit typed selections whenever row selection changes.
  // Skip the first render — initial state equals the initialJobIds/initialCtcJobNumbers
  // props, so firing on mount would spuriously mark the form dirty.
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const { jobIds, ctcJobNumbers } = splitKeys(Object.keys(rowSelection));
    onChange(
      jobIds,
      ctcJobNumbers.filter((n) => n !== ''),
    );

    // Retain DTOs for checked rows so they stay renderable after the search list changes.
    setSelectedOptions((prev) => {
      const pool = new Map<string, JobOptionDto>();
      for (const j of prev) pool.set(rowKey(j), j);
      for (const j of searchResults) pool.set(rowKey(j), j);
      return Object.keys(rowSelection)
        .map((key) => pool.get(key))
        .filter((j): j is JobOptionDto => j !== undefined);
    });
  }, [rowSelection]);

  // Selected rows are always shown; in browse mode they sit alongside the live search results.
  const displayedJobs = React.useMemo(() => {
    const selected = selectedOptions.filter((j) => rowSelection[rowKey(j)]);
    if (showOnlySelected) {
      return selected;
    }
    return mergeByKey(selected, searchResults);
  }, [searchResults, selectedOptions, showOnlySelected, rowSelection]);

  const columns = React.useMemo<MRT_ColumnDef<JobOptionDto>[]>(
    () => [
      {
        id: 'job_number',
        accessorFn: (row) =>
          row.source === SOURCE_CTC
            ? `${row.wsJobNumber ?? ''} - ${row.name ?? ''}`
            : `${row.jobNumber ?? ''} - ${row.name ?? ''}`,
        header: 'Job Number - Name',
        enableColumnActions: false,
      },
      {
        accessorKey: 'division',
        header: 'Division',
        enableColumnActions: false,
      },
      {
        id: 'source',
        header: 'Source',
        enableColumnActions: false,
        accessorFn: (row) => row.source,
        Cell: ({ cell }) =>
          cell.getValue<string>() === SOURCE_CTC ? (
            <Badge color="violet" variant="light" size="sm">
              CTC
            </Badge>
          ) : (
            <Badge color="blue" variant="light" size="sm">
              Insite
            </Badge>
          ),
      },
    ],
    [],
  );

  const tableState = useTableState<JobOptionDto>({
    identifier: 'job-grant-selector',
    columns: columns,
    defaultSorting: [{ id: 'job_number', desc: false }],
  });

  const tableExports = useTableExports<JobOptionDto>('jobs', (data) =>
    data.map((item) => ({
      ['Job Number']:
        item.source === SOURCE_CTC
          ? item.wsJobNumber ?? ''
          : item.jobNumber ?? '',
      ['Job Name']: item.name ?? '',
      ['Division']: item.division ?? '',
      ['Source']: item.source === SOURCE_CTC ? 'CTC' : 'Insite',
    })),
  );

  const { table } = useTable<JobOptionDto>({
    ...tableState,
    rowSelection: rowSelection,
    tableOptions: {
      enableStickyHeader: true,
      mantineTableContainerProps: {
        style: {
          maxHeight: '475px',
        },
      },
      enableRowSelection: true,
      enableRowActions: false,
      enableClickToCopy: false,
      enableColumnDragging: false,
      enableColumnResizing: false,
      enableTopToolbar: false,
      columns: columns,
      data: displayedJobs,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      onRowSelectionChange: setRowSelection,
    },
    getRowId: (row) => rowKey(row),
    totalCount: displayedJobs.length,
    isLoading: loading,
    isFetching: false,
    isError: false,
    ...tableExports,
  });

  return (
    <Stack>
      <Group align="flex-end">
        <TextInput
          placeholder="Search Jobs"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          leftSection={<IconSearch size={16} />}
          w={300}
        />
        <Checkbox
          label="Show Only Selected"
          checked={showOnlySelected}
          onChange={() => setShowOnlySelected(!showOnlySelected)}
        />
      </Group>
      {table}
    </Stack>
  );
};

/** Merge incoming results into existing options keyed by composite row key. */
function mergeByKey(
  existing: JobOptionDto[],
  incoming: JobOptionDto[],
): JobOptionDto[] {
  const map = new Map<string, JobOptionDto>();
  for (const job of existing) map.set(rowKey(job), job);
  for (const job of incoming) map.set(rowKey(job), job);
  return Array.from(map.values());
}
